import { useEffect, useMemo } from "react";
import { colors, transitions } from "../tokens/tokens";
import { motion, useAnimationControls } from "framer-motion";

export enum IconNames {
  arrowRight = "arrow right",
  arrowLeft = "arrow left",
  arrowDown = "arrow down",
  x = "x",
  xCircle = "x circle",
  beach = "beach",
  info = "info",
  carretUp = "carret up",
  carretDown = "carret down",
  callBack = "call back",
  calendarTime = "calendar time",
  calendar = "calendar",
  dotsButton = "dots button",
  checkInCircle = "check in circle",
  warning = "warning",
  search = "search",
  eye = "eye",
  chatUser = "chat user",
  download = "download",
  profilePic = "profile pic",
  phoneFull = "phone full",
  videoFull = "video full",
  timerFull = "timer full",
  chat = "chat",
  expand = "expand",
  collapse = "collapse",
  undo = "undo",
  plusCircle = "plus circle",
  caretRight = "caret right",
  caretLeft = "caret left",
  sendEnvelope = "send envelope",
  upload = "upload",
  share = "share",
  openCircle = "open circle",
  speaker = "speaker",
  editCard = "edit card",
  comment = "comment",
  commentFull = "comment full",
  sendEmail = "send email",
}

interface Props {
  name?: IconNames;
  stroke?: number;
  size?: number;
  animate?: boolean;
  fill?: string;
  theme?: "white" | "blue" | "orange" | string;
  onClick?: () => void;
  pointer?: boolean;
}

export default function Icon(props: Props) {
  const {
    name = IconNames.arrowLeft,
    size = 24,
    stroke = 1.5,
    animate = false,
    theme = "white",
    fill = "transparent",
    onClick = () => null,
    pointer = false,
  } = props;
  const controls = useAnimationControls();
  useEffect(() => {
    controls.start(theme);
  }, [theme, controls]);

  const renderIcon = useMemo(() => {
    const themeColors = {
      white: { stroke: colors.graysTextPrimary },
      blue: { stroke: colors.graysWhite },
      orange: { stroke: colors.graysTextPrimary },
      blueButton: { stroke: colors.primaryGPBlue },
      link: { stroke: colors.primaryGPBlue },
      system: { stroke: colors.grays400 },
      error: { stroke: colors.error },
      check: { stroke: colors.green500 },
      warning: { stroke: colors.warning300 },
      ok: { stroke: colors.green500 },
    };

    const themeFillColors = {
      white: { fill: colors.graysTextPrimary },
      blue: { fill: colors.graysWhite },
      orange: { fill: colors.graysTextPrimary },
      blueButton: { fill: colors.primaryGPBlue },
      link: { fill: colors.primaryGPBlue },
      system: { fill: colors.grays400 },
      error: { fill: colors.error },
      check: { fill: colors.green500 },
      warning: { fill: colors.warning300 },
      ok: { stroke: colors.green500 },
    };

    const icons = [
      {
        name: "send email",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <g transform="translate(3 4.028)">
              <path
                d="M 17 2.011 L 17 9 M 18 15 L 12 15 M 16 13 L 18 15 L 16 17 M 8 13 L 2 13 C 0.895 13 0 12.105 0 11 L 0 2.011"
                fill="transparent"
                strokeWidth={stroke}
                stroke={themeColors[theme].stroke}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
              ></path>
              <path
                d="M 2.011 0 L 14.99 0 C 16.1 0 17.001 0.9 17.001 2.011 L 17.001 2.011 C 17.001 2.678 16.67 3.301 16.118 3.675 L 10.761 7.306 C 9.396 8.231 7.604 8.231 6.239 7.306 L 0.883 3.676 C 0.331 3.302 0 2.679 0 2.012 L 0 2.011 C 0 0.9 0.9 0 2.011 0 Z"
                fill="transparent"
                strokeWidth={stroke}
                stroke={themeColors[theme].stroke}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
              ></path>
            </g>
            <path d="M 24 0.028 L 0 0.028 L 0 24.028 L 24 24.028 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "comment full",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path
              d="M 12 22.5 L 14.5 18 L 20 18 C 20.552 18 21 17.552 21 17 L 21 4 C 21 3.448 20.552 3 20 3 L 4 3 C 3.448 3 3 3.448 3 4 L 3 17 C 3 17.552 3.448 18 4 18 L 9.5 18 Z"
              fill="rgb(255, 221, 0)"
              stroke="rgb(254, 221, 4)"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
            <path
              d="M 7 8 L 17 8"
              fill="transparent"
              stroke-width="1.5"
              stroke="rgb(0, 0, 0)"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
            <path
              d="M 15 13 L 7 13"
              fill="transparent"
              stroke-width="1.5"
              stroke="rgb(0, 0, 0)"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "comment",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path
              d="M 15 21.513 L 15 18 L 20 18 C 20.552 18 21 17.552 21 17 L 21 4 C 21 3.448 20.552 3 20 3 L 4 3 C 3.448 3 3 3.448 3 4 L 3 17 C 3 17.552 3.448 18 4 18 L 10 18 Z M 15 13 L 7 13 M 7 8 L 17 8"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "edit card",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path
              d="M 9 21 L 5 21 L 5 21 C 3.895 21 3 20.105 3 19 L 3 19 L 3 5 L 3 5 C 3 3.895 3.895 3 5 3 L 17 3 C 18.105 3 19 3.895 19 5 L 19 8 M 7 8 L 13 8 M 7 12 L 9 12"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
            <path
              d="M 12 21 L 14.625 20.676 L 14.625 20.676 C 14.809 20.653 14.981 20.569 15.113 20.438 L 21.472 14.079 L 21.472 14.079 C 21.811 13.741 22.002 13.282 22.002 12.803 C 22.002 12.325 21.811 11.866 21.472 11.528 L 21.472 11.528 L 21.472 11.528 C 21.134 11.189 20.675 10.998 20.197 10.998 C 19.718 10.998 19.259 11.189 18.921 11.528 L 12.621 17.828 L 12.621 17.828 C 12.493 17.956 12.411 18.121 12.385 18.3 Z"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
          </svg>
        ),
      },
      {
        name: "speaker",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <g>
              <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
              <path
                d="M 5.875 8.625 L 3.5 8.625 C 2.948 8.625 2.5 9.073 2.5 9.625 L 2.5 14.375 C 2.5 14.927 2.948 15.375 3.5 15.375 L 5.875 15.375 L 9.854 18.746 C 10.151 18.998 10.567 19.054 10.921 18.89 C 11.274 18.726 11.5 18.372 11.5 17.983 L 11.5 6.017 C 11.5 5.165 10.503 4.703 9.854 5.254 Z M 15.537 15.978 C 16.699 15.256 17.5 13.754 17.5 12.005 C 17.5 10.256 16.699 8.753 15.537 8.022 M 18.505 19.065 C 20.313 17.51 21.5 14.93 21.5 12.003 C 21.5 9.072 20.311 6.49 18.5 4.936"
                fill="transparent"
                strokeWidth={stroke}
                stroke={themeColors[theme].stroke}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
              ></path>
            </g>
          </svg>
        ),
      },
      {
        name: "open circle",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path
              d="M 12 3 C 7.029 3 3 7.029 3 12 C 3 16.971 7.029 21 12 21 C 16.971 21 21 16.971 21 12 C 21 10.757 20.748 9.574 20.293 8.497"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "share",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path
              d="M 12.3 12.6 C 10.1 12.6 8.2 13.4 7.2 15.7 L 7.2 15 C 7.2 11.6 9.5 9.5 12.3 9.5 L 12.3 8 C 12.3 7.5 12.9 7.2 13.3 7.5 L 17 10.6 C 17.3 10.8 17.3 11.3 17 11.5 L 13.3 14.9 C 12.9 15.3 12.3 15 12.3 14.4 Z"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
            <path
              d="M 18.4 5.6 C 21.9 9.1 21.9 14.8 18.4 18.3 C 14.9 21.8 9.2 21.8 5.7 18.3 C 2.2 14.8 2.2 9.1 5.7 5.6 C 9.2 2.1 14.8 2.1 18.4 5.6"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
          </svg>
        ),
      },
      {
        name: "upload",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
            <path
              d="M 12 21 L 17 21 C 18.657 21 20 19.657 20 18 L 20 8.51 C 20 7.714 19.684 6.951 19.121 6.388 L 16.612 3.878 C 16.049 3.316 15.286 3 14.491 3 L 7 3 C 5.343 3 4 4.343 4 6 L 4 13"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <path
              d="M 6.6 15 L 9 17.5 L 6.6 20 M 3 17.5 L 9 17.5 M 20 8.5 L 16.5 8.5 C 15.395 8.5 14.5 7.605 14.5 6.5 L 14.5 3"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
          </svg>
        ),
      },
      {
        name: "send envelope",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path
              d="M 14.644 19.908 L 20.1 5.188 C 20.236 4.822 20.146 4.41 19.869 4.134 C 19.593 3.857 19.181 3.767 18.815 3.903 L 4.09 9.363 C 3.169 9.705 3.241 11.031 4.194 11.27 L 11.028 12.987 L 12.735 19.803 C 12.975 20.757 14.302 20.83 14.644 19.908 Z M 19.87 4.13 L 11.03 12.99"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "caret left",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
            <path
              d="M 12 6 L 6 0 L 0 6"
              transform="translate(6 9) rotate(-90 6 3)"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
          </svg>
        ),
      },
      {
        name: "caret right",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
            <path
              d="M 12 6 L 6 0 L 0 6"
              transform="translate(6 9) rotate(90 6 3)"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
          </svg>
        ),
      },
      {
        name: "calendar",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
            <path
              d="M 3 6 C 3 4.895 3.895 4 5 4 L 19 4 C 20.105 4 21 4.895 21 6 L 21 19 C 21 20.105 20.105 21 19 21 L 5 21 C 3.895 21 3 20.105 3 19 Z"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <path
              d="M 17 17 L 7 17 M 7.5 2.5 L 7.5 6 M 16.5 2.5 L 16.5 6"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
          </svg>
        ),
      },
      {
        name: "plus circle",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path
              d="M 12 8 L 12 16 M 16 12 L 8 12 M 12 21 L 12 21 C 7.029 21 3 16.971 3 12 L 3 12 C 3 7.029 7.029 3 12 3 L 12 3 C 16.971 3 21 7.029 21 12 L 21 12 C 21 16.971 16.971 21 12 21 Z"
              fill={fill}
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "check in circle",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path
              d="M 8.44 12.339 L 10.607 14.506 L 10.587 14.486 L 15.467 9.596"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <path
              d="M 12 3 C 7.029 3 3 7.029 3 12 C 3 16.971 7.029 21 12 21 C 16.971 21 21 16.971 21 12 C 21 7.029 16.971 3 12 3 Z"
              fill="transparent"
              strokeWidth={stroke}
              stroke={themeColors[theme].stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "undo",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <g transform="translate(5 5)">
              <path
                d="M 3 0 L 0 3 L 3 6"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
              ></path>
              <path
                d="M 0 3 L 9 3 C 12.314 3 15 5.462 15 8.5 L 15 8.5 C 15 11.538 12.314 14 9 14 L 1 14"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
              ></path>
            </g>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "collapse",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path
              d="M 15 8 L 18 8 M 15 12 L 18 12 M 15 16 L 18 16 M 12 4 L 12 20 L 19 20 C 20.105 20 21 19.105 21 18 L 21 6 C 21 4.895 20.105 4 19 4 L 5 4 C 3.895 4 3 4.895 3 6 L 3 12"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <g transform="translate(3 15) rotate(225 2 2.5)">
              <path
                d="M 4 5 L 0 5 L 0 1"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
              ></path>
              <path
                d="M 5 0 L 0 5"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
              ></path>
            </g>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "expand",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <path
              d="M 15 8 L 18 8 M 15 12 L 18 12 M 15 16 L 18 16 M 12 4 L 12 20 L 19 20 C 20.105 20 21 19.105 21 18 L 21 6 C 21 4.895 20.105 4 19 4 L 5 4 C 3.895 4 3 4.895 3 6 L 3 12"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></path>
            <g transform="translate(3 15) rotate(45 2.5 2.5)">
              <path
                d="M 4 5 L 0 5 L 0 1"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
              ></path>
              <path
                d="M 5 0 L 0 5"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
              ></path>
            </g>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "chat",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <motion.path
              d="M 18.5 18.564 L 18.5 20.5 C 18.5 20.703 18.379 20.885 18.191 20.963 C 18.004 21.04 17.789 20.997 17.646 20.854 L 15.358 18.565 M 18 9.5 L 18.504 9.5 C 19.883 9.5 21 10.617 21 11.996 L 21 16.068 C 21 17.447 19.883 18.564 18.504 18.564 L 18.504 18.564 M 15.362 18.564 L 12.496 18.564 C 11.117 18.564 10 17.447 10 16.068 L 10 15 M 8.706 12 L 6.354 14.353 C 6.211 14.496 5.996 14.539 5.809 14.462 C 5.621 14.384 5.5 14.202 5.5 13.999 L 5.5 12 L 5.496 12 C 4.117 12 3 10.883 3 9.504 L 3 5.496 C 3 4.117 4.117 3 5.496 3 L 12.504 3 C 13.883 3 15 4.117 15 5.496 L 15 9.504 C 15 10.883 13.883 12 12.504 12 Z"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
              variants={themeColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
            <path d="M 25 24 L 1 24 L 1 0 L 25 0 Z" fill="transparent"></path>
          </motion.svg>
        ),
      },
      {
        name: "phone full",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
            <motion.path
              d="M 3 11.918 C 3 7.004 7.076 2.918 11.991 2.918 C 16.915 2.918 21 7.004 21 11.918 C 21 16.842 16.924 20.918 12 20.918 C 7.086 20.918 3 16.842 3 11.918 Z M 10.384 13.953 C 12.166 15.752 14.445 16.889 15.789 15.544 C 15.84 15.492 15.878 15.44 15.922 15.388 C 16.263 15.001 16.36 14.481 15.915 14.147 C 15.558 13.887 15.18 13.626 14.564 13.188 C 14.111 12.876 13.814 12.95 13.472 13.292 L 13.168 13.589 C 13.087 13.678 12.931 13.663 12.827 13.604 C 12.56 13.44 12.114 13.084 11.683 12.653 C 11.26 12.229 10.889 11.769 10.733 11.509 C 10.689 11.419 10.652 11.285 10.749 11.181 L 11.045 10.862 C 11.402 10.498 11.468 10.223 11.149 9.769 L 10.206 8.439 C 9.895 7.985 9.42 8.075 8.907 8.455 C 8.863 8.484 8.833 8.514 8.803 8.543 C 7.46 9.896 8.596 12.17 10.384 13.954 Z"
              fill={colors.graysTextPrimary}
              variants={themeFillColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
          </motion.svg>
        ),
      },
      {
        name: "video full",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
            <motion.path
              d="M 3 11.918 C 3 7.004 7.076 2.918 11.991 2.918 C 16.915 2.918 21 7.004 21 11.918 C 21 16.842 16.924 20.918 12 20.918 C 7.086 20.918 3 16.842 3 11.918 Z M 12.847 15.018 C 13.586 15.018 14.017 14.585 14.017 13.855 L 14.017 10.187 C 14.017 9.444 13.63 9.018 12.883 9.018 L 8.936 9.018 C 8.225 9.018 7.759 9.444 7.759 10.187 L 7.759 13.855 C 7.759 14.585 8.197 15.018 8.936 15.018 Z M 15.883 14.274 C 16.012 14.39 16.163 14.455 16.292 14.455 C 16.572 14.455 16.759 14.253 16.759 13.935 L 16.759 10.101 C 16.759 9.79 16.572 9.588 16.292 9.588 C 16.163 9.588 15.997 9.653 15.883 9.762 L 14.433 11.003 L 14.433 13.032 Z"
              fill={colors.graysTextPrimary}
              variants={themeFillColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
          </motion.svg>
        ),
      },
      {
        name: "timer full",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
            <motion.path
              d="M 7.385 12.953 C 7.22 12.957 7.061 12.893 6.944 12.777 C 6.828 12.66 6.764 12.501 6.768 12.336 C 6.768 11.991 7.032 11.726 7.385 11.726 L 11.383 11.726 L 11.383 6.388 C 11.383 6.044 11.647 5.779 11.991 5.779 C 12.335 5.779 12.608 6.044 12.608 6.388 L 12.608 12.335 C 12.608 12.689 12.335 12.952 11.991 12.952 Z M 12.001 21 C 16.924 21 21 16.915 21 12 C 21 7.076 16.915 3 11.991 3 C 7.076 3 3 7.076 3 12 C 3 16.914 7.086 21 12 21 Z"
              fill={colors.graysTextPrimary}
              variants={themeFillColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
          </motion.svg>
        ),
      },

      {
        name: "profile pic",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <motion.path
              d="M 21.232 17.894 C 21.109 17.742 20.904 17.688 20.763 17.557 C 20.644 17.403 20.496 17.282 20.321 17.192 C 20.075 17.044 19.836 16.885 19.593 16.732 C 19.421 16.556 19.198 16.453 18.985 16.334 C 18.57 16.124 18.174 15.865 17.723 15.755 C 17.48 15.576 17.217 15.428 16.942 15.305 C 16.719 15.203 16.484 15.125 16.243 15.065 C 16.1 15.038 16.023 14.874 15.872 14.89 C 15.365 14.898 15.64 14.319 15.863 14.127 C 15.962 14.037 16.005 13.899 16.094 13.799 C 16.198 13.736 16.208 13.639 16.222 13.532 C 16.261 13.509 16.321 13.495 16.331 13.444 C 16.379 13.267 16.57 13.158 16.561 12.963 C 16.727 12.738 16.726 12.417 16.8 12.141 C 16.829 11.774 16.913 11.443 16.913 11.076 C 16.92 10.977 16.976 10.92 17.062 10.884 C 17.337 10.78 17.608 10.502 17.43 10.208 C 17.377 9.972 17.416 9.722 17.414 9.481 C 17.374 9.045 17.788 7.891 17.402 7.644 C 17.187 7.585 17.309 7.327 17.303 7.174 C 17.361 7.093 17.324 7.015 17.314 6.93 C 17.362 6.783 17.349 6.625 17.349 6.472 C 17.333 6.379 17.276 6.294 17.289 6.195 C 17.276 6.028 17.185 5.876 17.211 5.703 C 17.214 5.62 17.218 5.532 17.165 5.456 C 17.109 5.381 17.19 5.289 17.111 5.226 C 17.169 5.149 17.105 5.064 17.145 4.981 C 17.223 4.843 17.046 4.877 16.989 4.81 C 16.967 4.736 16.926 4.661 16.999 4.589 C 17.073 4.507 16.967 4.227 16.97 4.102 C 16.924 4.018 16.882 4.146 16.877 3.97 C 16.837 3.957 16.809 4.012 16.771 3.975 C 16.738 3.949 16.72 3.913 16.726 3.871 C 16.797 3.566 16.601 3.948 16.638 3.689 C 16.65 3.502 16.455 3.739 16.455 3.535 C 16.47 3.384 16.409 3.244 16.261 3.186 C 16.212 3.203 16.244 3.273 16.192 3.277 C 16.182 3.277 16.164 3.265 16.163 3.256 C 16.146 3.149 16.048 3.09 16.011 2.993 C 15.971 2.866 15.878 2.765 15.827 2.643 C 15.772 2.457 15.573 2.393 15.483 2.232 C 15.433 2.147 15.426 2.321 15.333 2.159 C 15.276 2.048 15.118 2.02 15.109 1.88 C 15.087 1.749 14.899 1.761 14.815 1.673 C 14.651 1.51 14.403 1.504 14.234 1.357 C 14.2 1.324 14.162 1.312 14.128 1.353 C 14.082 1.321 13.928 1.307 13.943 1.241 C 13.887 1.296 13.789 1.214 13.737 1.178 C 13.74 1.25 13.646 1.192 13.609 1.184 C 13.524 1.149 13.429 1.15 13.342 1.113 C 13.307 1.103 13.273 1.091 13.264 1.05 C 13.202 1.035 13.121 1.065 13.069 1.031 C 13.071 1.164 12.863 1.033 12.773 1.038 C 12.585 0.96 12.388 1.031 12.194 1.009 C 12.05 1.015 11.932 1.125 11.813 1.191 C 11.716 1.187 11.659 1.293 11.561 1.273 C 11.331 1.291 11.126 1.427 10.908 1.496 C 10.789 1.553 10.687 1.651 10.549 1.658 C 10.524 1.663 10.508 1.686 10.513 1.71 C 10.617 1.886 10.314 1.826 10.332 1.986 C 10.431 2.134 10.176 2.106 10.108 2.192 C 10.022 2.21 9.921 2.202 9.833 2.233 C 9.669 2.241 9.575 2.406 9.418 2.443 C 9.311 2.488 9.259 2.601 9.147 2.644 C 8.979 2.728 8.991 2.91 9.059 3.056 C 9.117 3.223 8.884 3.145 8.926 3.27 C 8.945 3.298 8.964 3.338 8.954 3.37 C 8.888 3.577 8.671 3.677 8.465 3.698 C 8.483 3.718 8.494 3.731 8.506 3.744 C 8.423 3.825 8.419 3.93 8.422 4.032 C 8.386 4.076 8.328 4.049 8.29 4.079 C 8.269 4.148 8.343 4.131 8.37 4.162 C 8.31 4.253 8.176 4.162 8.249 4.32 C 8.389 4.582 8.103 4.526 8.106 4.849 C 8.153 4.893 8.206 4.928 8.186 4.997 C 8.162 5.289 8.311 5.56 8.387 5.834 C 8.529 6.223 8.407 6.662 8.557 7.056 C 8.566 7.348 8.696 7.615 8.727 7.903 C 8.742 8.02 8.615 8.078 8.604 8.188 C 8.434 8.589 8.675 9.052 8.714 9.468 C 8.773 9.939 8.815 10.412 8.771 10.883 C 8.771 11.176 9.089 11.161 9.174 11.354 C 9.21 11.443 9.21 11.533 9.216 11.624 C 9.273 11.695 9.238 11.791 9.284 11.866 C 9.308 12.105 9.402 12.336 9.448 12.572 C 9.481 12.756 9.587 12.921 9.64 13.103 C 9.694 13.203 9.705 13.315 9.775 13.407 C 9.963 13.737 9.494 13.967 9.274 14.137 C 9.248 14.156 9.222 14.168 9.19 14.16 C 9.093 14.143 9.012 14.21 8.927 14.245 C 8.611 14.352 8.36 14.375 8.084 14.595 C 7.843 14.697 7.606 14.807 7.372 14.922 C 7.094 15.071 6.865 15.29 6.571 15.415 C 6.226 15.555 5.895 15.747 5.591 15.959 C 5.484 16.098 5.11 16.253 4.952 16.323 L 3.343 17.369 C 2.899 17.716 2.608 17.988 2.2 18.427 C 4.669 21.227 8.324 23 12.404 23 C 16.264 23 19.745 21.412 22.2 18.869 C 21.769 18.413 21.232 17.894 21.232 17.894 Z"
              fill={colors.graysTextPrimary}
              variants={themeFillColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </motion.svg>
        ),
      },
      {
        name: "download",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <g>
              <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
              <motion.path
                d="M 12 17 L 12 3 M 20 21 L 4 21 M 17 12 L 11.999 17.001 L 6.999 12"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
            </g>
          </motion.svg>
        ),
      },
      {
        name: "chat user",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <motion.path
              d="M 12 7.222 C 13.105 7.222 14 8.117 14 9.222 C 14 10.327 13.105 11.222 12 11.222 C 10.895 11.222 10 10.327 10 9.222 C 10 8.117 10.895 7.222 12 7.222 Z M 16 15 C 15.884 14.707 15.703 14.445 15.472 14.231 C 15.082 13.869 14.57 13.667 14.038 13.667 L 9.963 13.667 C 9.097 13.666 8.319 14.195 8 15"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
              variants={themeColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
            <motion.path
              d="M 14 19 L 18 19 C 19.657 19 21 17.657 21 16 L 21 6 C 21 4.343 19.657 3 18 3 L 6 3 C 4.343 3 3 4.343 3 6 L 3 16 C 3 17.657 4.343 19 6 19 L 10 19 L 12 21 Z"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
              variants={themeColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
          </motion.svg>
        ),
      },
      {
        name: "eye",
        svg: (
          <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
            <g transform="translate(3 5)">
              <motion.path
                d="M 11.122 4.88 C 12.293 6.053 12.293 7.952 11.122 9.125 C 9.949 10.296 8.05 10.296 6.877 9.125 C 5.706 7.952 5.706 6.053 6.877 4.88 C 7.44 4.317 8.203 4 8.999 4 C 9.796 4 10.559 4.317 11.122 4.88"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
              <motion.path
                d="M 0 7 C 0 6.341 0.152 5.689 0.446 5.088 L 0.446 5.088 C 1.961 1.991 5.309 0 9 0 C 12.691 0 16.039 1.991 17.554 5.088 L 17.554 5.088 C 17.848 5.689 18 6.341 18 7 C 18 7.659 17.848 8.311 17.554 8.912 L 17.554 8.912 C 16.039 12.009 12.691 14 9 14 C 5.309 14 1.961 12.009 0.446 8.912 L 0.446 8.912 C 0.153 8.317 0.001 7.663 0 7 Z"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
            </g>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </svg>
        ),
      },
      {
        name: "search",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <g>
              <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
              <motion.path
                d="M 15.714 6.838 C 18.165 9.289 18.165 13.263 15.714 15.714 C 13.263 18.165 9.289 18.165 6.838 15.714 C 4.387 13.263 4.387 9.289 6.838 6.838 C 9.289 4.387 13.263 4.387 15.714 6.838 M 19 19 L 15.71 15.71"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
            </g>
          </motion.svg>
        ),
      },
      {
        name: "warning",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <g>
              <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
              <motion.path
                d="M 12 3 L 12 3 C 16.971 3 21 7.029 21 12 L 21 12 C 21 16.971 16.971 21 12 21 L 12 21 C 7.029 21 3 16.971 3 12 L 3 12 C 3 7.029 7.029 3 12 3 Z M 12 12.5 L 12 7.5"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
              <motion.path
                d="M 11.999 16 C 11.861 16.001 11.749 16.113 11.75 16.251 C 11.751 16.389 11.863 16.501 12.001 16.5 C 12.139 16.5 12.251 16.388 12.251 16.25 C 12.25 16.111 12.138 16 12 16"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
            </g>
          </motion.svg>
        ),
      },
      {
        name: "dots button",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <g transform="translate(3 3.8)">
              <motion.path
                d="M 0 4 C 0 1.791 1.791 0 4 0 L 14 0 C 16.209 0 18 1.791 18 4 L 18 12 C 18 14.209 16.209 16 14 16 L 4 16 C 1.791 16 0 14.209 0 12 Z"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
              <motion.path
                d="M 9.25 8 L 9.25 8 C 9.25 8.138 9.138 8.25 9 8.25 C 8.862 8.25 8.75 8.138 8.75 8 C 8.75 7.862 8.862 7.75 9 7.75 L 9 7.75 C 9.138 7.75 9.25 7.862 9.25 8 M 5.25 7.992 L 5.25 7.992 C 5.25 8.13 5.138 8.242 5 8.242 C 4.862 8.242 4.75 8.13 4.75 7.992 C 4.75 7.854 4.862 7.742 5 7.742 L 5 7.742 C 5.138 7.742 5.25 7.854 5.25 7.992 M 13.25 7.982 L 13.25 7.982 C 13.25 8.12 13.138 8.232 13 8.232 C 12.862 8.232 12.75 8.12 12.75 7.982 C 12.75 7.844 12.862 7.732 13 7.732 L 13 7.732 C 13.138 7.732 13.25 7.844 13.25 7.982"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
            </g>
            <path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></path>
          </motion.svg>
        ),
      },
      {
        name: "calendar",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <g>
              <motion.path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></motion.path>
              <motion.path
                d="M 15 3 L 15 5 M 7 3 L 7 5 M 3 9 L 19 9 M 19 10 L 19 7 C 19 5.343 17.657 4 16 4 L 6 4 C 4.343 4 3 5.343 3 7 L 3 16 C 3 17.657 4.343 19 6 19 L 10 19 M 17.393 15.983 L 17.393 17.741 L 18.775 18.584"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
              <motion.path
                d="M 17.5 22 C 15.015 22 13 19.985 13 17.5 C 13 15.066 15.07 12.997 17.504 13 C 19.989 13.001 22.003 15.017 22.002 17.502 C 22.001 19.987 19.985 22.001 17.5 22"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeMiterlimit="10"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
            </g>
          </motion.svg>
        ),
      },
      {
        name: "call back",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <motion.path
              d="M 10.131 13.869 C 9.024 12.773 8.13 11.481 7.493 10.06 C 7.373 9.776 7.439 9.448 7.659 9.233 L 8.478 8.414 C 9.149 7.743 9.149 6.794 8.563 6.208 L 7.39 5.035 C 6.609 4.254 5.343 4.254 4.562 5.035 L 3.91 5.686 C 3.169 6.427 2.86 7.496 3.06 8.556 C 3.554 11.169 5.072 14.03 7.521 16.479 C 9.97 18.928 12.831 20.446 15.444 20.94 C 16.504 21.14 17.573 20.831 18.314 20.09 L 18.965 19.439 C 19.746 18.658 19.746 17.392 18.965 16.611 L 17.792 15.438 C 17.206 14.852 16.257 14.852 15.671 15.438 L 14.768 16.342 C 14.553 16.562 14.225 16.628 13.941 16.508 C 12.52 15.87 11.228 14.975 10.131 13.869 Z M 15 3 L 15 9 L 21 9 M 21 3 L 15 9"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
              variants={themeColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
            <motion.path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></motion.path>
          </motion.svg>
        ),
      },
      {
        name: "carret down",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <motion.path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></motion.path>
            <motion.path
              d="M 12 6 L 6 0 L 0 6"
              transform="translate(6 9) rotate(180 6 3)"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
              variants={themeColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
          </motion.svg>
        ),
      },
      {
        name: "arrow down",
        svg: (
          <motion.svg
            variants={themeColors}
            initial={theme}
            transition={{ ...transitions.themeChange }}
            animate={controls}
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <motion.path
              d="M 11.5 4.5 L 0 4.5 M 7 9 L 11.5 4.5 M 7 0 L 11.5 4.5"
              transform="translate(6 8) rotate(90 5.75 4.5)"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
            ></motion.path>
            <motion.path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></motion.path>
          </motion.svg>
        ),
      },
      {
        name: "carret up",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <g>
              <motion.path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></motion.path>
              <motion.path
                d="M 18 15 L 12 9 L 6 15"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
            </g>
          </motion.svg>
        ),
      },
      {
        name: "arrow right",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <motion.path
              d="M 19 12 L 5 12 M 14 17 L 19 12 M 14 7 L 19 12"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
              variants={themeColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
            <motion.path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></motion.path>
          </motion.svg>
        ),
      },
      {
        name: "arrow left",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <motion.path
              d="M 5 12 L 19 12 M 10 7 L 5 12 M 10 17 L 5 12"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
              variants={themeColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
            <motion.path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></motion.path>
          </motion.svg>
        ),
      },
      {
        name: "x circle",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <g>
              <motion.path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></motion.path>
              <motion.path
                d="M 14.83 9.17 L 9.17 14.83 M 14.83 14.83 L 9.17 9.17 M 12 21 L 12 21 C 7.029 21 3 16.971 3 12 L 3 12 C 3 7.029 7.029 3 12 3 L 12 3 C 16.971 3 21 7.029 21 12 L 21 12 C 21 16.971 16.971 21 12 21 Z"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
            </g>
          </motion.svg>
        ),
      },
      {
        name: "x",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <g>
              <motion.path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></motion.path>
              <motion.path
                d="M 8 8 L 16 16 M 16 8 L 8 16"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
            </g>
          </motion.svg>
        ),
      },
      {
        name: "beach",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <motion.path
              d="M 12 5 L 12 3 M 2 13 L 4 13 M 4.89 5.89 L 6 7 M 19.11 5.89 L 18 7 M 22 12.88 L 20 12.88 M 3 17.382 L 3 17.382 C 4.146 16.873 5.454 16.873 6.6 17.382 L 6.6 17.382 C 7.746 17.891 9.054 17.891 10.2 17.382 L 10.2 17.382 C 11.346 16.873 12.654 16.873 13.8 17.382 L 13.8 17.382 C 14.946 17.891 16.254 17.891 17.4 17.382 L 17.4 17.382 C 18.546 16.873 19.854 16.873 21 17.382 L 21 17.382 M 6.6 20.618 L 6.6 20.618 C 7.746 21.127 9.054 21.127 10.2 20.618 L 10.2 20.618 C 11.346 20.109 12.654 20.109 13.8 20.618 L 13.8 20.618 C 14.946 21.127 16.254 21.127 17.4 20.618 L 17.4 20.618 M 7 13 C 7 10.239 9.239 8 12 8 C 14.761 8 17 10.239 17 13"
              fill="transparent"
              strokeWidth={stroke}
              stroke={colors.graysTextPrimary}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray=""
              variants={themeColors}
              initial={theme}
              transition={{ ...transitions.themeChange }}
              animate={controls}
            ></motion.path>
            <motion.path d="M 0 0 L 24 0 L 24 24 L 0 24 Z" fill="transparent"></motion.path>
          </motion.svg>
        ),
      },
      {
        name: "info",
        svg: (
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
          >
            <g>
              <motion.path d="M 24 24 L 0 24 L 0 0 L 24 0 Z" fill="transparent"></motion.path>
              <motion.path
                d="M 3 12 C 3 7.029 7.029 3 12 3 C 16.971 3 21 7.029 21 12 C 21 16.971 16.971 21 12 21 C 7.029 21 3 16.971 3 12 Z"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
              <motion.path
                d="M 11 15.5 L 13.31 15.5 M 12.159 15.5 L 12.159 11.25 L 11.009 11.25 M 12.1 8.247 L 12.1 8.247 C 12.1 8.385 11.988 8.497 11.85 8.497 C 11.712 8.497 11.6 8.385 11.6 8.247 C 11.6 8.109 11.712 7.997 11.85 7.997"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
              <motion.path
                d="M 11.85 7.997 L 11.85 7.997 C 11.988 7.997 12.1 8.109 12.1 8.247"
                fill="transparent"
                strokeWidth={stroke}
                stroke={colors.graysTextPrimary}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray=""
                variants={themeColors}
                initial={theme}
                transition={{ ...transitions.themeChange }}
                animate={controls}
              ></motion.path>
            </g>
          </motion.svg>
        ),
      },
    ];

    let item = icons.filter((data) => name.toString() === data.name);
    return item ? item[0].svg : null;
  }, [theme, size, name, stroke, controls]);

  const wrapperStyle = {
    display: "inline",
    verticalAlign: "middle",
    width: size,
    height: size,
    cursor: pointer ? "pointer" : "default",
  };

  const repeatTransition = {
    repeat: Infinity,
    repeatType: "mirror" as any,
    repeatDelay: 2,
    duration: 1,
    ease: "easeInOut",
  };

  return (
    <motion.div
      style={wrapperStyle}
      onClick={() => onClick()}
      initial={animate ? { y: 0 } : null}
      transition={animate ? repeatTransition : null}
      animate={animate ? { y: [0, 4, 0, 4, 0] } : ""}
    >
      {renderIcon}
    </motion.div>
  );
}
